// frontend/src/components/CampaignManager/tabs/VersandTab.tsx
import React, { useState, useMemo } from 'react';
import {
  Box, Button, Stack, Typography, TableContainer, Table, TableHead, TableRow,
  TableCell, TableBody, Paper, IconButton, Chip, useMediaQuery, useTheme, Alert, TablePagination
} from '@mui/material';
import { PaperPlaneTilt, PencilSimple, Trash } from '@phosphor-icons/react';
import { VersandCard } from '../MobileCards';
import { Versand, Campaign } from '../types';

// Strip lottery suffix from campaign name (e.g. "Inaktive - SKL 147" -> "Inaktive")
const stripLotterySuffix = (name: string): string => {
  if (!name) return name;
  return name.replace(/\s+-\s+(SKL|NKL|GKL)\s+\d+$/i, '').trim();
};

interface Props {
  versand: Versand[];
  campaigns: Campaign[];
  deleteVersand: (id: number) => Promise<void>;
  onOpenDialog: (versand: Versand | null) => void;
}

export default function VersandTab({ versand, campaigns, deleteVersand, onOpenDialog }: Props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);

  const paginatedVersand = useMemo(() => {
    const start = page * rowsPerPage;
    return versand.slice(start, start + rowsPerPage);
  }, [versand, page, rowsPerPage]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Versand wirklich löschen?')) {
      await deleteVersand(id);
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6">Versand</Typography>
        <Button
          variant="contained"
          size={isMobile ? 'small' : 'medium'}
          startIcon={<PaperPlaneTilt size={20} />}
          onClick={() => onOpenDialog(null)}
          sx={{ backgroundColor: '#003366' }}
          disabled={campaigns.length === 0}
        >
          {isSmallMobile ? 'Neu' : 'Neuer Versand'}
        </Button>
      </Stack>

      {campaigns.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Bitte erstellen Sie zuerst eine Kampagne, bevor Sie einen Versand anlegen.
        </Alert>
      )}

      {isMobile ? (
        versand.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">Keine Versand-Einträge vorhanden</Typography>
          </Paper>
        ) : (
          <>
            {paginatedVersand.map((vs) => (
              <VersandCard key={vs.id} versand={vs} onEdit={onOpenDialog} onDelete={handleDelete} />
            ))}
            {versand.length > rowsPerPage && (
              <TablePagination
                component="div"
                count={versand.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[50, 100, 250, 500]}
                labelRowsPerPage="Pro Seite:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} von ${count}`}
              />
            )}
          </>
        )
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Kampagne</TableCell>
                <TableCell>Werbeweg</TableCell>
                <TableCell>Versandbrief</TableCell>
                <TableCell>Inhalt</TableCell>
                <TableCell>Porto</TableCell>
                <TableCell>Kl.</TableCell>
                <TableCell>Gutschrift</TableCell>
                <TableCell align="right">Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedVersand.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      Keine Versand-Einträge vorhanden
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedVersand.map((vs) => (
                  <TableRow key={vs.id} hover>
                    <TableCell><strong>{stripLotterySuffix(vs.kampagne_name || '') || `Kampagne ${vs.kampagnen_id}`}</strong></TableCell>
                    <TableCell>{vs.werbeweg_name || `Werbeweg ${vs.werbeweg_id}`}</TableCell>
                    <TableCell>{vs.versandbrief || '-'}</TableCell>
                    <TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{vs.inhalt_versandbrief || '-'}</TableCell>
                    <TableCell>{vs.porto ? `${Number(vs.porto).toFixed(2)} €` : '-'}</TableCell>
                    <TableCell>{vs.klasse_beginn || '-'}</TableCell>
                    <TableCell>
                      {vs.gutschrift_betrag ? (
                        <Chip label={`${Number(vs.gutschrift_betrag).toFixed(2)} €`} size="small" color="success" />
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => onOpenDialog(vs)}>
                        <PencilSimple size={18} />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDelete(vs.id)}>
                        <Trash size={18} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={versand.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[50, 100, 250, 500]}
            labelRowsPerPage="Pro Seite:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} von ${count}`}
          />
        </TableContainer>
      )}
    </Box>
  );
}
